import React from 'react'
import firebase from 'firebase'
import Table from 'antd/lib/table'
import Form from 'antd/lib/form'
import Input from 'antd/lib/input'
import Button from 'antd/lib/button'
import 'antd/lib/table/style/index.css'
import 'antd/lib/form/style/index.css'
import 'antd/lib/input/style/index.css'
import 'antd/lib/button/style/index.css'
import { WrappedFormUtils } from 'antd/lib/form/Form'

import { ConfirmButton, ReAuth } from '~/components'

import {
  addWalletAddress,
  auth,
  disableWallet,
  ERROR_REAUTH_REQUIRED,
  getAccountRef,
  PROVIDERS,
  PROVIDERS_IDS,
  saveNote,
  updateWalletsSelected,
} from '~/firebase'
import { WalletAddress } from '~/types'

import './style.css'

// https://firebase.google.com/docs/auth/web/manage-users
// https://firebase.google.com/docs/auth/web/account-linking merge existing users


class WalletTable extends Table<WalletAddress> {}
class WalletColumn extends Table.Column<WalletAddress> {}


export default class Account extends React.Component {
  state = {
    note: null as string | null,
    showChangePassword: false,
    showDisabledWallets: false,
    showFormAddWallet: false,
    showFormLinkCredentials: false,
    showLinkAccounts: false,
    showNoteButtons: false,
    showReAuth: false as boolean | string,
    wallets: [] as WalletAddress[],
  }

  accountRef = getAccountRef()
  user = auth.currentUser!

  componentDidMount() {
    this.accountRef.on('value', this.mergeIntoState)
  }

  componentWillUnmount() {
    this.accountRef.off()
  }

  mergeIntoState = (snapshot: firebase.database.DataSnapshot) => {
    const newState = {
      note: snapshot.child('note').val() || '',
      wallets: [],
    } as Pick<this['state'], 'wallets' | 'note'>

    snapshot.child('wallets').forEach(item => {
      const {description, disabled, disabledTimestamp} = item.val()

      newState.wallets.push({
        key: item.key,
        ...item.val(),
        description: disabled
          ? `${description}\nDisabled at ${new Date(disabledTimestamp!).toLocaleString()}`
          : description,
      })
      return false// return false in firebase.forEach to not break out of loop
    })

    this.setState(newState)
  }

  hideReAuth = () => this.setState({showReAuth: false})
  showReAuth = message => this.setState({showReAuth: message})

  toggleFormAddWallet = () => this.setState({showFormAddWallet: !this.state.showFormAddWallet})
  toggleChangePassword = () => this.setState({showChangePassword: !this.state.showChangePassword})
  toggleLinkAccounts = () => this.setState({showLinkAccounts: !this.state.showLinkAccounts})
  toggleFormLinkCredentials = () => this.setState({showFormLinkCredentials: !this.state.showFormLinkCredentials})

  onAddWalletAddress = ev => {
    const {description, type, walletAddr} = ev.currentTarget.form.elements

    if (description.value.length > 255 || walletAddr.value.length > 100) return alert('Exceeded maximum characters allowed.')

    addWalletAddress({
      description: description.value,
      type: type.value,
      walletAddress: walletAddr.value,
    } as WalletAddress)

    this.toggleFormAddWallet()
  }

  onUpdateWalletsChecked = (ev: React.SyntheticEvent<HTMLInputElement>) => {
    const t = ev.currentTarget
    const payload = {}

    if (t.form!.elements[t.name].length) {
      [].forEach.call(t.form!.elements[t.name], el => payload[el.value + '/selected'] = el.checked)
    } else {
      payload[t.form!.elements[t.name].value + '/selected'] = true
    }

    updateWalletsSelected(payload)
  }

  onDisableWallet = (ev: React.SyntheticEvent<HTMLButtonElement>) => disableWallet(ev.currentTarget.dataset.value!)

  onSaveNote = (ev: React.SyntheticEvent<HTMLButtonElement>) => saveNote(ev.currentTarget.form!.elements['note'].value!)
    .then(() => this.setState({showNoteButtons: false}))

  // updateProfile() {
  //   this.user.updateProfile({
  //     displayName: "Jane Q. User",
  //     photoURL: "https://example.com/jane-q-user/profile.jpg"
  //   }).then(function() {
  //     // Update successful.
  //   }).catch(function(error: Error) {
  //     // An error happened.
  //   })
  // }

  catchFirebaseError = er => {
    if (er.code === ERROR_REAUTH_REQUIRED) {
      this.showReAuth(er.message)
    } else {
      this.forceUpdate()
      alert('Error. See datails in console.')
      console.error(er) // tslint:disable-line:no-console
    }
  }

  updateEmail = (form: WrappedFormUtils) => {
    this.user.updateEmail(form.getFieldValue('email')).then(() => {
      this.forceUpdate()
    }).catch(this.catchFirebaseError)
  }

  updatePassword = (form: WrappedFormUtils) => {
    const p1 = form.getFieldValue('password')
    const p2 = form.getFieldValue('confirm')

    if (p1 === p2) {
      this.user.updatePassword(p1).then(() => {
        this.toggleChangePassword()
      }).catch(this.catchFirebaseError)
    } else {
      console.error('passwords don\'t match') // tslint:disable-line:no-console
    }
  }

  linkProvider = ev => {
    const ix = ev.currentTarget.dataset.ix!

    // when linking EmailAuthProvider, user must type password twice
    if (ix === 'password_provided') {// <button dataset-ix='password_provided'
      const {email: {value: email}, password: {value: password}} = ev.currentTarget.form.elements

      const credentials = firebase.auth.EmailAuthProvider.credential(email, password)
      this.user.linkWithCredential(credentials).then((/* user */) => {
        this.toggleFormLinkCredentials()
      }).catch(this.catchFirebaseError)

      return
    }

    // else

    switch (PROVIDERS_IDS[ix]) {
      case firebase.auth.EmailAuthProvider.PROVIDER_ID:
        this.toggleFormLinkCredentials()
        // display email password inputs and confirm button
        break
      // case firebase.auth.PhoneAuthProvider
      default:
        this.user.linkWithPopup(new PROVIDERS[ev.currentTarget.dataset.ix!]()).then(() => {
          this.forceUpdate()
        }).catch(this.catchFirebaseError)
    }
  }

  unlinkProvider = (ev: React.SyntheticEvent<HTMLInputElement>) => {
    if (this.user.providerData.length === 1) return console.error('Cannot unlink last provider.') // tslint:disable-line

    this.user.unlink(PROVIDERS_IDS[ev.currentTarget.dataset.ix!]).then(() => {
      this.forceUpdate()
    }).catch(this.catchFirebaseError)
  }

  // Wallets ----------------------------------------------------------------------------------------------------------

  renderWallets = () => (
    <form name="walletTable">
      <WalletTable
        className="walletTable" size="small"
        dataSource={this.state.wallets.filter(r => this.state.showDisabledWallets || !r.disabled)}
        rowKey="addedTimestamp"
        rowClassName={r => r.disabled ? 'disabledRow' : ''}
        footer={this.renderFormAddWallet}
        pagination={false}
      >
        <WalletColumn key="type" title="Type" dataIndex="type" width={80}/>
        <WalletColumn key="walletAddress" title="Address" dataIndex="walletAddress" width={360}/>
        <WalletColumn key="description" title="Note" dataIndex="description"/>
        <WalletColumn
          key="addedTimestamp" title="Date added" dataIndex="addedTimestamp" render={this.renderWalletDate} width={140}
        />
        <Table.Column title="Actions" width={110} render={(row: WalletAddress) => (
          <>
            <input
              type="radio" className="switch"
              title={`Select${row.selected ? 'ed' : ''} for payments`}
              disabled={row.disabled}
              value={row.key} name={row.type} defaultChecked={row.selected}
              onChange={this.onUpdateWalletsChecked}
            />
            <ConfirmButton size="small" ghost type="danger" className="ant-btn-dashed"
              disabled={row.disabled}
              onClick={this.onDisableWallet} data-value={row.key}>Delete</ConfirmButton>
          </>
        )}/>
      </WalletTable>
    </form>
  )

  renderWalletDate = (date: number) => (
    <>
      {new Date(date).toLocaleDateString(undefined, {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'})/* tslint:disable-line */}
      <br/>
      {new Date(date).toLocaleTimeString(undefined, {hour: '2-digit', minute:'2-digit'})}
    </>
  )

  renderFormAddWallet = () => (
    this.state.showFormAddWallet
      ? (
        // <form>
        <table>
          <colgroup>
            <col style={{width: 80}}/>
            <col style={{width: 360}}/>
            <col/>
            <col style={{width: 60}}/>
          </colgroup>
          <tfoot>
            <tr><td/><td colSpan={3}>Make sure to provide correct address.</td></tr>
            <tr className="ant-table-row ant-table-row-level-0">
              <td>
                <select id="type">
                  <option value="ETH">ETH</option>
                  <option value="ZCASH">ZCASH</option>
                  <option value="LTC">LTC</option>
                </select>
              </td>
              <td><input id="walletAddr" type="text" placeholder="wallet address" maxLength={100}/></td>
              <td><input id="description" type="text" placeholder="notes" maxLength={255}/></td>
              <td>
                <button title="save" type="button" onClick={this.onAddWalletAddress}>✓</button>
                <button title="cancel" type="button" onClick={this.toggleFormAddWallet}>✗</button>
              </td>
            </tr>
          </tfoot>
        </table>
        // </form>
      )
      : <button title="Add new wallet address" onClick={this.toggleFormAddWallet}>+</button>
  )

  // Profile forms ----------------------------------------------------------------------------------------------------

  emailForm = Form.create({
    // onFieldsChange?: (props: T, fields: Array<any>) => void
    // onValuesChange?: (props: T, values: any) => void
    // mapPropsToFields?: (props: T) => void
    // withRef?: boolean
    mapPropsToFields: () => ({
      email: Form.createFormField({
        value: this.user.email,
      }),
    }),
  })(({form}) => (
    <Form className="emailForm" layout="inline">
      <Form.Item label="Email">
        {form.getFieldDecorator('email', {
          initialValue: this.user.email,
          // getValueFromEvent: this.onEmailChange,
          rules: [
            { type: 'email', message: 'The input is not valid E-mail!' },
            { required: true, message: 'Please input your E-mail!'},
          ],
        })(<Input/>)}
      </Form.Item>
      {form.isFieldsTouched() && (
      <>
        <button title="save" onClick={() => this.updateEmail(form)} disabled={!!form.getFieldError('email')}>✓</button>
        <button title="cancel" onClick={() => form.resetFields()}>✗</button>
      </>
      )}
    </Form>
  ))


  passwordForm = Form.create({
    mapPropsToFields: () => ({
      confirm: Form.createFormField({}),
      password: Form.createFormField({}),
    }),
  })(({form}) => (
    <Form className="passwordForm" layout="inline">
      <Form.Item label="Password">
        {form.getFieldDecorator('password', {
          rules: [{
            message: 'Password must contain at least 6 characters!',
            min: 6,
            required: true,
          }, {
            validator: (rule, value, callback) => {
              if (value /* && this.state.confirmDirty */) {
                form.validateFields(['confirm'], { force: true } as any)
              }
              callback()
            },
          }],
        })(
          <Input type="password" />
        )}
      </Form.Item>
      <Form.Item label="Confirm Password">
        {form.getFieldDecorator('confirm', {
          rules: [{
            message: 'Please confirm your password!',
            required: true,
          }, {
            validator: (rule, value, callback) => {
              if (value && value !== form.getFieldValue('password')) callback('Passwords must match!')
              else callback()
            },
          }],
        })(
          <Input type="password"/>
        )}
      </Form.Item>
      <button disabled={!!form.getFieldError('confirm')} title="save" onClick={() => this.updatePassword(form)}>✓</button> {/* tslint:disable-line */}
      <button title="cancel" onClick={this.toggleChangePassword}>✗</button>
    </Form>
  ))

  renderChangePassword = () => (
    this.state.showChangePassword
      ? <this.passwordForm/>
      : <article className="profileToggleSection"><Button onClick={this.toggleChangePassword}>Change password</Button></article> // tslint:disable-line
  )

  // Link accounts ----------------------------------------------------------------------------------------------------

  renderLinkAccounts = () => (
    this.state.showLinkAccounts
      ? (
        <article className="ant-form ant-form-inline">
          {PROVIDERS_IDS.map((provider, ix) => {
            const isAlreadyLinked = this.user.providerData.some(userProvider => userProvider!.providerId === provider)
            return (
              <label key={provider} style={{marginRight: 40}}>
                <input
                  type="checkbox"
                  className="switch"
                  data-ix={ix}
                  // defaultChecked={isAlreadyLinked}
                  checked={isAlreadyLinked}
                  onClick={isAlreadyLinked ? this.unlinkProvider : this.linkProvider}
                  title={isAlreadyLinked ? 'unlink' : undefined}
                />
                {provider}</label>
            )
          })}
          <button title="cancel" onClick={this.toggleLinkAccounts}>✗</button>
        </article>
      )
      : <article className="profileToggleSection"><Button onClick={this.toggleLinkAccounts}>Link Accounts</Button></article> // tslint:disable-line
  )

  renderFormLinkCredentials = () => this.state.showFormLinkCredentials ? (
    <article>
      <form>
        <input type="email" name="email"/>
        <input type="password" name="password"/>
        <button title="save" type="button" data-ix="password_provided" onClick={this.linkProvider}>✓</button>
        <button title="cancel" type="button" onClick={this.toggleFormLinkCredentials}>✗</button>
      </form>
    </article>
  ) : null

  // Notes ------------------------------------------------------------------------------------------------------------

  showNoteButtons = () => !this.state.showNoteButtons && this.setState({showNoteButtons: true})

  renderFormNote = () => this.state.note !== null ? (
    <form>
      <Input.TextArea
        name="note"
        autosize={{minRows: 4, maxRows: 10}}
        defaultValue={this.state.note} onChange={this.showNoteButtons}
      />
      { this.state.showNoteButtons && <Button type="primary" onClick={this.onSaveNote}>Save</Button> }
    </form>
  ) : null

  // Render -----------------------------------------------------------------------------------------------------------

  render() {
    const {showReAuth, showDisabledWallets} = this.state

    return (
      <div className="isoLayoutContentWrapper sc-fjdhpX cKswoM" style={{height: '100%'}}>
        <main className="sc-iRbamj gXBuxZ">

          <section>
            <h2 className="isoComponentTitle sc-jzJRlG kviPuT">Wallets</h2>

            <label><input
              type="checkbox" className="switch"
              defaultChecked={showDisabledWallets}
              onChange={() => this.setState({showDisabledWallets: !showDisabledWallets})}
            />[DEBUG_SHOW_DELETED_WALLETS]</label>
            <this.renderWallets/>
          </section>

          <section className="profile">
            <h2 className="isoComponentTitle sc-jzJRlG kviPuT">Profile</h2>
            <this.emailForm/>
            <this.renderChangePassword/>
            <this.renderLinkAccounts/>
            <this.renderFormLinkCredentials/>
          </section>

          <section className="Notes" style={{marginTop: 40}}>
            <h2 className="isoComponentTitle sc-jzJRlG kviPuT">Notes</h2>
            <this.renderFormNote/>
          </section>

          {showReAuth && <ReAuth message={showReAuth as string} onSuccess={this.hideReAuth}/>}

        </main>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/Account/index.tsx